import {
  membersApi,
  type Branch,
  type BranchMember,
  type MemberPermissionQuery,
  type MembersFilter,
  resolveMembershipBranchIdForMapping,
} from '@/entities/Branch/@x/User';
import type { Membership, MembershipItem, User } from './types';

export { resolveMembershipBranchIdForMapping };
const pendingUsersByBranch = new Map<number, Promise<User[]>>();
export function mapBranchMemberToUser(member: BranchMember, branchId: number): User | null {
  if (member.id == null || !member.user?.id) {
    return null;
  }

  const { user } = member;
  const positionRows = (member.positions ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    active: true as const,
    type: '',
    createdAt: '',
    updatedAt: '',
  }));

  const membershipItem: MembershipItem = {
    id: member.id,
    branchId,
    userId: user.id,
    positionId: positionRows[0]?.id,
    createdAt: '',
    updatedAt: '',
    positions: positionRows,
    branch: { id: branchId, name: '' },
    functionalActivities: (member.functionalActivities ?? []).map((fa) => ({
      functionalActivity: { id: fa.id, name: fa.name },
    })),
  };

  const membershipList: Membership = {
    id: member.id,
    branchId,
    userId: user.id,
    branchName: '',
    positionName: positionRows[0]?.name ?? '',
    permissions: [],
    branch: { id: branchId, name: '' } satisfies Branch,
    createdAt: '',
    updatedAt: '',
    positionId: positionRows[0]?.id,
    position: positionRows[0]
      ? {
          id: positionRows[0].id,
          name: positionRows[0].name,
          createdAt: '',
          updatedAt: '',
          active: true,
        }
      : undefined,
    positions: positionRows.map((p) => ({ id: p.id, name: p.name })),
    functionalActivities: membershipItem.functionalActivities,
  };

  return {
    id: user.id,
    name: user.name,
    email: user.email,
    registrationNumber: user.registrationNumber ?? undefined,
    membership: membershipItem,
    memberships: [membershipList],
  };
}

/** Loads branch members (GET /members, session context) mapped to {@link User} for selectors. */
export async function fetchAllMembersAsUsers(
  membershipBranchId?: number,
  permissionQuery?: MemberPermissionQuery,
  filter?: MembersFilter
): Promise<User[]> {
  const branchId = resolveMembershipBranchIdForMapping(membershipBranchId);
  if (branchId === undefined) {
    return [];
  }

  if (permissionQuery || filter) {
    // Filtered/permissioned requests intentionally bypass the in-flight dedupe cache below.
    // Coalescing would require keying the cache on branchId + a hash of permissionQuery/filter;
    // given how rarely two identical filtered requests race (e.g. a single filter modal
    // double-mounting), that complexity isn't worth the correctness risk of a bad cache key.
    const filtered = await membersApi.getAllMembersPaginated(100, undefined, permissionQuery, filter);
    return filtered
      .map((member) => mapBranchMemberToUser(member, branchId))
      .filter((user): user is User => Boolean(user));
  }

  const pending = pendingUsersByBranch.get(branchId);
  if (pending) {
    return pending;
  }

  const request = (async () => {
    const combined = await membersApi.getAllMembersPaginated(100);
    const users: User[] = [];
    for (const m of combined) {
      const u = mapBranchMemberToUser(m, branchId);
      if (u) {
        users.push(u);
      }
    }
    return users;
  })();

  pendingUsersByBranch.set(branchId, request);

  try {
    return await request;
  } finally {
    pendingUsersByBranch.delete(branchId);
  }
}
